<template>
  <div class="translate-sidebar">
    <!-- 导航菜单 -->
    <div class="sidebar-menu">
      <div
        v-for="item in menuItems"
        :key="item.key"
        class="menu-item"
        :class="{ active: activeMenu === item.key }"
        @click="handleMenuClick(item.key)"
      >
        <WorkbenchIcon :name="item.icon" :size="15" />
        <span class="menu-text">{{ item.name }}</span>
        <span
          v-if="item.key === 'enterprise' && hasPendingInvite"
          class="enterprise-notice-dot"
          aria-hidden="true"
        ></span>
      </div>
    </div>

    <!-- 使用量显示 -->
    <div class="sidebar-usage">
      <div class="usage-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: usagePercentage + '%' }"></div>
        </div>
      </div>
      <div class="usage-info">
        <span class="usage-text">{{ usageText }}</span>
        <!-- <a href="#" class="usage-link">{{$t}}</a> -->
      </div>
      
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import useUserStore from '~/store/modules/user'
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import { hasPendingEnterpriseInvitation } from '@/utils/enterpriseManagement'
import { getEnterpriseQuotaSnapshot } from '@/utils/enterpriseTrade'
const userStore = useUserStore()
const { bizTeamInfo, userInfo } = storeToRefs(userStore)
const {t} = useI18n()
interface MenuItem {
  key: string
  name: string
  icon: string
}

interface Props {
  activeMenu: string
}

interface Emits {
  (e: 'menuChange', menu: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const quotaSnapshot = computed(() => getEnterpriseQuotaSnapshot(bizTeamInfo.value))
const hasPendingInvite = computed(() => hasPendingEnterpriseInvitation(userInfo.value))

// 菜单项配置
const menuItems = computed<MenuItem[]>(() => [
  { key: 'home', name: t('account_menu_home'), icon: 'house' },
  { key: 'image', name: t('product_content_tab_title_2'), icon: 'image' },
  { key: 'enterprise', name: t('account_menu_enterprise'), icon: 'building-2' },
  { key: 'records', name: t('account_menu_translate'), icon: 'file-clock' },
  { key: 'consumption', name: t('account_menu_cost'), icon: 'history' },
  { key: 'glossary', name: t('account_menu_art'), icon: 'book-marked' },
  { key: 'orders', name: t('account_menu_orders'), icon: 'receipt' },
  { key: 'combo', name: t('account_menu_combo'), icon: 'package' },
  { key: 'support', name: t('enterprise_priority_support_title'), icon: 'headset' }
])

// 使用量计算
const usagePercentage = computed(() => {
  let char = getCurrentComboChar()
  let charAll = getCurrentComboAllChar()
  return charAll > 0 ? Math.min(100, Math.max(0, (char / charAll) * 100)) : 0
})

const usageText = computed(() => (
  t('enterprise_ui_used_quota', { used: getCurrentComboChar().toLocaleString(), total: getCurrentComboAllChar().toLocaleString() })
))

// 获取所有已使用的
const getCurrentComboChar = () => {
  return quotaSnapshot.value.usedWords
}

const getCurrentComboAllChar = () => {
  return quotaSnapshot.value.totalWords
}

// 处理菜单点击
const handleMenuClick = (menuKey: string) => {
  emit('menuChange', menuKey)
}
</script>

<style lang="scss" scoped>
.translate-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;
}

.sidebar-header {
  padding: 24px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-img {
  width: 32px;
  height: 32px;
}

.logo-text {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.sidebar-menu {
  flex: 1;
  padding: 14px 10px;
}

.menu-item {
  display: flex;
  align-items: center;
  min-height: 37px;
  padding: 9px 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  gap: 10px;
  margin-bottom: 4px;
  color: #51565d;
  border-radius: 6px;
  &:hover {
    background-color: #f8f9fa;
  }

  &.active {
    color: #1f2329;
    background-color: #f2f4f7;

    .menu-text {
      color: #1f2329;
      font-weight: 600;
    }
  }
}

.menu-icon {
  width: 20px;
  height: 20px;
  font-size: 18px;
}

.menu-text {
  font-size: 13px;
  color: #51565d;
  line-height: 19px;
}

.enterprise-notice-dot {
  width: 7px;
  height: 7px;
  margin-left: auto;
  flex: 0 0 auto;
  border-radius: 50%;
  background-color: #f54a45;
}

.sidebar-usage {
  box-sizing: border-box;
  width: 164px;
  min-height: 46px;
  padding: 10px 6px;
  margin: 0 10px;
  border-top: 1px solid #f0f0f0;
}

.usage-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}

.usage-text {
  font-size: 10.5px;
  color: #98a2b3;
  line-height: 15px;
  white-space: nowrap;
}

.usage-link {
  font-size: 12px;
  color: #1890ff;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.usage-progress {
  width: 100%;
}

.progress-bar {
  width: 100%;
  height: 5px;
  background-color: #e8ebf0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #1890ff;
  border-radius: 2px;
  transition: width 0.3s ease;
}

</style>
